/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useMemo, useState, useEffect } from 'react';
import './styles/DashboardSideNavigationBar.scss';
import { Link, useLocation } from 'react-router-dom';

import { Badge, Divider, Stack, Typography } from '@mui/material';
import { ArrowDropDown, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight, SwapHoriz } from '@mui/icons-material';

import { IMenuItem } from '@dw/models/shared';
import { Button, HStack, IconButton } from '@dw/components/core';
import { ROUTES, SCREEN_SIZE } from '@dw/constants';
import { classNames, componentClassName, formatQueryParamString } from '@dw/utils';
import { useAuth, useBinaryToggle, useRouteData, useScreenSize } from '@dw/hooks';
import { useGetMyPendingDanceSchoolInvites } from '@dw/hooks/api/DanceSchoolUserApi';

import { INavMenuItem } from './types';

const cn = componentClassName('dashboard-side-navigation-bar');

interface IDashboardSideNavigationBarProps {
    menuItems: INavMenuItem[];
    menuItemSelected: (menuItem: IMenuItem) => void;
}
export const DashboardSideNavigationBar: FunctionComponent<IDashboardSideNavigationBarProps> = (props: IDashboardSideNavigationBarProps) => {

    const { menuItems, menuItemSelected } = props;

    const { queryParamData } = useRouteData();
    const location = useLocation();
    const { currentSize, isLargeDesktop } = useScreenSize();
    const { danceSchool } = useAuth();
    const { data: pendingInvites } = useGetMyPendingDanceSchoolInvites();

    // The school footer is where switching schools lives, and joining one is the same idea, so an
    // invite waiting for an answer is announced there rather than anywhere new.
    const pendingInviteCount = pendingInvites?.length ?? 0;

    const { isOpen: isCollapsed, toggle: toggleIsCollapsed } = useBinaryToggle();

    const [activeNavItemPath, setActiveNavItemPath] = useState(ROUTES.ManageMyProfile);
    const menuItemsToUse = useMemo(() => {
        return menuItems.filter(x => (!x.data || x.data === 'SIDE_NAV') && !x.hidden);
    }, [menuItems]);

    const wrapAsLinkIfOne = (item: INavMenuItem, element: React.ReactNode) => item.routerLink ? (
        <Link key={item.text} to={item.routerLink}>
            {element}
        </Link>
    ) : item.routerQueryParams ? (
        <Link key={item.text} to={{
            search: formatQueryParamString(item.routerQueryParams)
        }}>
            {element}
        </Link>
    ) : element;

    const withBadge = (item: INavMenuItem, element: React.ReactNode): React.ReactNode => item.badgeValue ? (
        <Badge
            badgeContent={item.badgeValue}
            color={item.badgeColor ?? 'secondary'}
            sx={{ display: 'block', width: '100%' }}
            slotProps={{ badge: { style: { top: '50%', right: 12 } } }}
        >
            {element}
        </Badge>
    ) : element;

    const isChildActive = (childItem: INavMenuItem): boolean => {
        const routerLinkMatch = Boolean(childItem.routerLink) && location.pathname.toLowerCase().startsWith(childItem.routerLink!.toLowerCase());
        const routerQueryParamsMatch = Boolean(childItem.routerQueryParams) && Object.keys(childItem.routerQueryParams!).filter(x => queryParamData[x] === childItem.routerQueryParams![x]).length === Object.keys(childItem.routerQueryParams!).length;

        return routerLinkMatch || routerQueryParamsMatch || Boolean(childItem.isActive);
    }

    const buildMenuItemChildren = (parentItem: INavMenuItem): React.ReactNode => {
        const filteredChildItems = (parentItem.childItems ?? []).filter(x => (!x.data || x.data === 'SIDE_NAV') && !x.hidden);
        if (!parentItem.showChildItems || filteredChildItems.length < 1){
            return null;
        }

        return (
            <Stack paddingLeft={isCollapsed ? 0 : currentSize >= SCREEN_SIZE.XL ? 2 : 1.5}>
                {filteredChildItems.map((x, index) => x.isDivider ? (
                    <Divider key={index} sx={{
                        borderColor: 'rgba(var(--color-light-rgb), 0.3)'
                    }} />
                ) : wrapAsLinkIfOne(x,
                    <>
                        {withBadge(x, isCollapsed ? (
                            <IconButton
                                icon={x.icon}
                                color='light'
                                key={x.text}
                                size={'medium'}
                                sx={{
                                    justifyContent: 'flex-start',
                                    fontWeight: isChildActive(x) ? 'bold' : 'normal',
                                    opacity: isChildActive(x) ? 1 : 0.7
                                }}
                                tooltip={x.text}
                                onClick={() => !x.routerLink && !x.routerQueryParams && menuItemSelected ? menuItemSelected(parentItem) : null }
                            />
                        ) : (
                            <Button
                                startIcon={x.icon}
                                variant='text'
                                color='light'
                                key={x.text}
                                size={isLargeDesktop ? 'medium' : 'small'}
                                sx={{
                                    width: '100%',
                                    justifyContent: 'flex-start',
                                    fontWeight: isChildActive(x) ? 'bold' : 'normal',
                                    opacity: isChildActive(x) ? 1 : 0.7
                                }}
                                onClick={() => !x.routerLink && !x.routerQueryParams && menuItemSelected ? menuItemSelected(parentItem) : null }
                            >
                                {x.text}
                            </Button>
                        ))}

                        {buildMenuItemChildren(x)}
                    </>
                ))}
            </Stack>
        )
    }

    const buildMenuItem = (menuItem: INavMenuItem, index: number): React.ReactNode => {
        if (menuItem.isDivider) {
            return <Divider key={index} sx={{
                borderColor: 'rgba(var(--color-light-rgb), 0.3)'
            }} />
        }

        const buttonElement = wrapAsLinkIfOne(menuItem, withBadge(menuItem, isCollapsed ? (
            <IconButton
                icon={menuItem.icon}
                color='light'
                key={menuItem.text}
                size={'medium'}
                sx={{
                    justifyContent: 'flex-start',
                    fontWeight: activeNavItemPath === menuItem.routerLink ? 'bold' : 'normal'
                }}
                onClick={() => !menuItem.routerLink && menuItemSelected ? menuItemSelected(menuItem) : null }
                tooltip={menuItem.text}
            />
        ) : (
            <Button
                startIcon={menuItem.icon}
                variant='text'
                color='light'
                key={menuItem.text}
                size={isLargeDesktop ? 'medium' : 'small'}
                sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    fontWeight: activeNavItemPath === menuItem.routerLink ? 'bold' : 'normal'
                }}

                onClick={() => !menuItem.routerLink && menuItemSelected ? menuItemSelected(menuItem) : null }
            >
                {menuItem.text}
            </Button>
        )));

        return (
            <Stack key={menuItem.text}>
                {buttonElement}
                {buildMenuItemChildren(menuItem)}
            </Stack>
        )
    }

    useEffect(() => {
        const pathName = location.pathname;
        const matchingItem = menuItemsToUse
            .filter(x => x.routerLink)
            .find(x => pathName.toLowerCase().startsWith(x.routerLink!.toLowerCase()));
        setActiveNavItemPath(matchingItem?.routerLink ?? '');
    }, [location, menuItemsToUse]);

    return (
        <Stack rowGap={2} className={classNames('dw-dashboard-side-navigation-bar', 'pointer', {
            [cn('wide')]: !isCollapsed && currentSize === SCREEN_SIZE.XL,
            [cn('narrow')]: !isCollapsed && currentSize < SCREEN_SIZE.XL,
            [cn('collapsed')]: isCollapsed,
        })}>
            {menuItemsToUse.map(buildMenuItem)}

            <div className={cn('collapser')}>
                <IconButton
                    size='small'
                    color='light'
                    icon={isCollapsed ? <KeyboardDoubleArrowRight /> : <KeyboardDoubleArrowLeft fontSize='small' />}
                    onClick={() => toggleIsCollapsed()}
                />
            </div>

            {danceSchool && (
                <HStack
                    breakpoint={false}
                    columnGap={0.5}
                    justifyContent='center'
                    className={cn('current-school-footer')}
                    onClick={() => {
                        menuItemSelected({
                            value: 'SWITCH'
                        })
                    }}
                >
                    {/*
                      The badge goes INSIDE the footer, around its content. Wrapping the footer
                      itself puts the badge's anchor in normal flow while the footer is
                      `position: fixed` to the bottom of the viewport - so it detaches and floats
                      up the sidebar instead of sitting on the bar.
                    */}
                    <Badge
                        badgeContent={pendingInviteCount}
                        color='error'
                        slotProps={{ badge: { style: { top: 4, right: -6 } } }}
                    >
                        <HStack breakpoint={false} columnGap={0.5} alignItems='center'>
                            {isCollapsed ? (
                                <IconButton
                                    icon={<SwapHoriz />}
                                    color='light'
                                />
                            ) : (
                                <>
                                    <Typography className='no-wrap' variant='body3'>{danceSchool?.name}</Typography>
                                    <ArrowDropDown fontSize='small' />
                                </>
                            )}
                        </HStack>
                    </Badge>
                </HStack>
            )}
        </Stack>
    )
};